import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  LockOutlined,
  LoginOutlined,
  PersonOutline,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/api';
import logo from '../assets/logo.svg';
import { usePortalText } from '../locales/portalTranslations';

const Login = ({ onLoginSuccess, notice = '' }) => {
  const { t } = useTranslation();
  const pt = usePortalText();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authService.login(formData.username, formData.password);
      if (response.status !== 'success') throw new Error(response.message);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('username', formData.username);
      onLoginSuccess?.(response.data);
    } catch (loginError) {
      setError(loginError.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendRecovery = async event => {
    event.preventDefault();
    setRecoveryLoading(true);
    try {
      const response = await authService.forgotPassword(email);
      setRecoveryMessage(response.message);
    } catch (requestError) {
      setRecoveryMessage('The request could not be completed. Please try again shortly.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: { xs: 3, sm: 4 } }}>
      <Container maxWidth="xs" disableGutters>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src={logo} alt="Motor Inspection" sx={{ height: 56, width: 'auto', mb: 2 }} />
          <Typography component="h1" variant="h2" color="text.primary">{t('login.title')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{t('login.subtitle')}</Typography>
        </Box>

        <Paper sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider', borderRadius: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          {notice && <Alert severity="info" role="status" sx={{ mb: 3 }}>{notice}</Alert>}
          {error && <Alert severity="error" role="alert" sx={{ mb: 3 }}>{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} aria-busy={loading}>
            <TextField
              required
              fullWidth
              autoFocus
              id="username"
              name="username"
              label={t('login.username')}
              autoComplete="username"
              value={formData.username}
              onChange={event => setFormData(current => ({ ...current, username: event.target.value }))}
              sx={{ mb: 2 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline fontSize="small" color="action" /></InputAdornment> }}
            />
            <TextField
              required
              fullWidth
              id="password"
              name="password"
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={formData.password}
              onChange={event => setFormData(current => ({ ...current, password: event.target.value }))}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>
                      {showPassword ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button size="small" onClick={() => { setForgotOpen(true); setRecoveryMessage(''); }}>{pt('forgotPassword')}</Button>
            </Box>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginOutlined fontSize="small" />}
            >
              {loading ? 'Signing in…' : t('login.signIn')}
            </Button>
          </Box>
        </Paper>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 3 }}>
          © {new Date().getFullYear()} {t('login.copyright')}
        </Typography>
      </Container>
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={sendRecovery}>
          <DialogTitle>{pt('resetPassword')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {pt('resetHelp')}
            </Typography>
            {recoveryMessage && <Alert severity="info" sx={{ mb: 2 }}>{recoveryMessage}</Alert>}
            <TextField required fullWidth autoFocus type="email" label={pt('accountEmail')} autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setForgotOpen(false)}>{pt('cancel')}</Button>
            <Button type="submit" variant="contained" disabled={recoveryLoading}>{recoveryLoading ? pt('sending') : pt('sendReset')}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Login;
